import React, {
  useCallback, useState, useRef, useEffect, useMemo,
} from 'react'

const Hydrator = ({
  Component, lazy, initialHeight = '100px', initialStyle, ...rest
}) => {
  const lazyLoad = __BROWSER__ && lazy

  const observerRef = useRef(null)
  const [hydrateNow, setHydrateNow] = useState(false)

  const delayedHydrate = useCallback(root => {
    if (!root) return
    /**
     * checking whether the component came inside viewport
     */
    observerRef.current = new IntersectionObserver(([entry], observer) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      observerRef.current = null
      setHydrateNow(true)
    })

    observerRef.current.observe(root)
  }, [setHydrateNow])

  const divStyle= useMemo(() => ({ height: initialHeight, ...initialStyle }),
    [initialHeight, initialStyle])

  useEffect(() => () => {
    if (observerRef.current) {
      /**
       * disconnecting the observer if the component is unmounted before hydration
       */
      observerRef.current.disconnect()
    }
  }, [])

  const style = useMemo(() => ({
    height: initialHeight,
    ...(initialStyle || {}) }),
  [initialHeight, initialStyle])

  if (!lazyLoad || hydrateNow) {
    /**
     * this props spread is needed as we are intending to make this a generic component
     */
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Component {...rest} />
  }

  return (
    <div
     /**
      * initial height is needed to delay the hydration properly.
      * if these div does not have height then all of them will come to viewport instantly
      * causing hydration of all of them at the same time.
      */
      style={divStyle}
      suppressHydrationWarning
      ref={delayedHydrate}
      /**
       * needed dangerouslySetInnerHTML to delay the hydration process as in this scenario
       * react will not diff between server html and client html
       */
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: '' }}
    />
  )
}

export default Hydrator
